import styled from 'styled-components/macro';
import { PrimaryColors } from 'src/types/globalTypes';
import DropdownInfo from 'src/components/ui-kit/DropdownInfo';
import { Table, Title, Content } from 'src/components/contents/detail/DetailPage';
import { TmarkRightsDetail } from 'src/types/api/trademarkRightsApiTypes';
import ZoomImages from 'src/components/ui-kit/ZoomImages';
import TmarkImages from 'src/components/contents/detail/tmarkDetail/TmarkImages';
import { useState } from 'react';
import { mobilePoint } from 'src/styles/globalStyle';
import RightsEffectiveTag, {
  RightsEffectStatus,
} from 'src/components/layout/tag/RightsEffectiveTag';
import { stringToDate } from 'src/utils/formatters';
import PdfDownloadTmark from './PdfDownloadTmark';
import { CertPdfChkDetail } from 'src/types/api/patentRightsApiTypes';
import TmarkMedia from '../TmarkMedia';

// 商標基礎區塊(有圖片區)
const TmarkBaseDiv = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  @media (max-width: ${mobilePoint}) {
    display: flex;
    flex-flow: wrap-reverse;
    justify-content: center;
  }
`;
const TmarkInfo = styled.div`
  display: grid;
  gap: 8px;
  height: fit-content;
`;
const TmarkBaseItem = styled.div`
  display: grid;
  grid-template-columns: 2fr 7fr;
  min-height: 38px;
  div:last-child {
    border-right: none !important;
  }
  @media (max-width: ${mobilePoint}) {
    grid-template-columns: 2fr 3fr;
  }
`;

interface Iprops {
  data: TmarkRightsDetail;
  certPdfChkDetail: CertPdfChkDetail;
  isShow: boolean;
  setIsShow: (isShow: boolean) => void;
  colorType: PrimaryColors;
  noContent: JSX.Element;
}

/**
 * 商標案件基本資訊。
 */
const InfoTmarkRightBase: React.FC<Iprops> = ({
  data,
  certPdfChkDetail,
  isShow,
  setIsShow,
  colorType,
  noContent,
}) => {
  // 圖片區塊控制
  const [isZoomOpen, setIsZoomOpen] = useState<boolean>(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  const tableColor = colorType.third;
  const angleColor = colorType.fifth;
  const SEPRATOR = ', '; //同格有複數資料的間隔符號

  // 圖片相關資訊
  const { audioFilename, ...allImageUrls } =
    data.tmarkImageUrls.length !== 0
      ? data.tmarkImageUrls[0]
      : {
          imageData1: '',
          imageData2: '',
          imageData3: '',
          imageData4: '',
          imageData5: '',
          imageData6: '',
          audioFilename: '',
        };
  const imageUrls = Object.values(allImageUrls).filter((url) => !!url); // 非null的圖片網址陣列
  const imageData = {
    tmarkDraftC: data.tmarkDraftC,
    tmarkDraftE: data.tmarkDraftE,
    tmarkDraftJ: data.tmarkDraftJ,
    tmarkSign: data.tmarkSign,
  };

  // 取得權利當前狀態
  const rightsStatus = () => {
    let result = RightsEffectStatus.None;
    if (data.regNoticeDate && data.deadline) {
      const tmp_expired = stringToDate(data.deadline);
      const now = new Date();
      if (data.delReason) {
        result = RightsEffectStatus.InEffective;
      } else if (!data.delReason && tmp_expired && tmp_expired.getTime() >= now.getTime()) {
        result = RightsEffectStatus.Effective;
      }
    }
    return result;
  };

  return (
    <>
      {isZoomOpen && imageUrls && imageUrls.length > 0 && (
        <ZoomImages
          isOpen={isZoomOpen}
          setClose={() => setIsZoomOpen(false)}
          imagesUrls={imageUrls}
          activeImageIdx={activeImageIdx}
          data={imageData}
        />
      )}
      <DropdownInfo
        title={`商標註冊號數：${data.examNo}`}
        isShow={isShow}
        setIsShow={setIsShow}
        color={angleColor}
      >
        <TmarkBaseDiv>
          <TmarkInfo>
            <Table color={tableColor}>
              <TmarkBaseItem>
                <Title>商標名稱</Title>
                <Content>{data.tmarkName || noContent}</Content>
              </TmarkBaseItem>
              <TmarkBaseItem>
                <Title>商標權人</Title>
                <Content>
                  {data.holders.length !== 0
                    ? data.holders.map((item, index) => {
                        index += 1;
                        return index !== data.holders.length
                          ? item.chineseName + '、'
                          : item.chineseName;
                      })
                    : noContent}
                </Content>
              </TmarkBaseItem>
              <TmarkBaseItem>
                <Title>商品或服務類別資訊</Title>
                <Content>
                  {data.goodsClasses.length !== 0 &&
                  data.goodsClasses[0].goodsClassCode &&
                  data.goodsClasses[0].goodsClassCode !== '　'
                    ? data.goodsClasses.map((item, index) => {
                        index += 1;
                        return index !== data.goodsClasses.length
                          ? item.goodsClassCode + SEPRATOR
                          : item.goodsClassCode;
                      })
                    : noContent}
                </Content>
              </TmarkBaseItem>
              <TmarkBaseItem>
                <Title>商標權利期間</Title>
                <Content>
                  自{data.regNoticeDate}至{data.deadline}止
                  <RightsEffectiveTag status={rightsStatus()} content={data.delReason} />
                </Content>
              </TmarkBaseItem>
            </Table>
            <Table color={tableColor}>
              <TmarkBaseItem>
                <Title>商標證書</Title>
                <Content>
                  <PdfDownloadTmark
                    data={data}
                    certPdfChkDetail={certPdfChkDetail}
                    colorType={colorType}
                    noContent={noContent}
                  />
                </Content>
              </TmarkBaseItem>
            </Table>
          </TmarkInfo>
          <div>
            {imageUrls && (
              <TmarkImages
                imageUrls={imageUrls}
                setIsZoomOpen={setIsZoomOpen}
                activeImageIdx={activeImageIdx}
                setActiveImageIdx={setActiveImageIdx}
              />
            )}
            {audioFilename && <TmarkMedia audioFilename={audioFilename} color={colorType.forth} />}
          </div>
        </TmarkBaseDiv>
      </DropdownInfo>
    </>
  );
};

export default InfoTmarkRightBase;
